import { useEffect, useState } from "react";
import axios from "axios";
import { Badge, Button, Col, FloatingLabel, Row, Form } from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const FamilyFundForecast = () => {
  const navigate = useNavigate();
  // With an id the selected families are added to that existing request
  const { id: requestId } = useParams();
  const isAddMode = Boolean(requestId);

  // Store people data fetched by selected rows
  const [forecasts, setForecasts] = useState([]);

  // Request details asked when the request is created
  const [donors, setDonors] = useState([]);
  const [donorId, setDonorId] = useState("");
  const [totalFamilies, setTotalFamilies] = useState("");
  const [rfqId, setRfqId] = useState("");
  const [existingCount, setExistingCount] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Set authorization header globally
  const AuthToken = localStorage.getItem("AuthUser");
  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  // Initialize forecasts from location state
  useEffect(() => {
    const stored = sessionStorage.getItem("selectedRows");

    if (stored) {
      const selectedIds = JSON.parse(stored); // already an array of IDs [2, 4, 5]

      axios.post(`${process.env.REACT_APP_API_URL}/family-forecast`, {
        selected_ids: selectedIds,
      })
        .then((response) => {
          const forecasts = response.data.map((person) => ({
            id: person.id,
            fsf_id: person.f_number || 0,
            familyIdentifier: person?.identifier?.name ||0,
            brickKiln: person?.brick_kiln?.brickkiln_name || 0,
            brickKilnOwnerName: person?.brick_kiln?.brickkiln_owner || 0,
            brickKilnOwnerCell: person?.brick_kiln?.brickkiln_owner_cell || 0,
            brickKilnOwnerCnic: person?.brick_kiln?.owner_cnic || 0,
            name: person.beneficiary_name || 0,
            cnic: person.cnic ||0,
            loanAmount: person?.loan?.loan_amount || 0,
            transportationAmount: person?.loan?.transportation || 0,
            businessAmount: person?.loan?.estimated_business_cost || 0,
            scholarshipAmount: person.loan?.scholarship_amount || 0,
            partnerIncentive: person.loan?.incentive || 0,
            medical_amount: person.loan?.medical_amount || 0,
            food_ration: person.loan?.food_ration || 0,
            meal_amount: person.loan?.meal_amount || 0,
            utility_amount: person.loan?.utility_amount || 0,
            rent_amount: person.loan?.rent_amount || 0,
            affidavit_amount: person.loan?.affidavit_amount || 0,
            other: person.loan?.other || 0,
          }));

          setForecasts(forecasts);
        })
        .catch((error) => {
          console.error("Error fetching forecasts:", error);
        });

    } else {
      console.warn("No selectedRows in sessionStorage");
    }
  }, []);

  // Donors and Request ID (new request), or the request families are being added to
  useEffect(() => {
    if (isAddMode) {
      axios.get(`${process.env.REACT_APP_API_URL}/edit-forecast/${requestId}`)
        .then((res) => {
          setDonors(res.data.donors);
          setRfqId(res.data.forecast?.rfq_id);
          setDonorId(String(res.data.forecast?.loan_donor_id || ""));
          setTotalFamilies(String(res.data.forecast?.requested_families || ""));
          setExistingCount(res.data.families_count);
          setIsLocked(res.data.is_locked);
          setRequestStatus(res.data.forecast?.status);
        })
        .catch((error) => {
          console.error("Error fetching request:", error);
        });
    } else {
      axios.get(`${process.env.REACT_APP_API_URL}/forecast-initial-data`)
        .then((res) => {
          setDonors(res.data.donors);
          setRfqId(res.data.next_rfq_id);
          if (res.data.donors.length === 1) {
            setDonorId(String(res.data.donors[0].id));
          }
        })
        .catch((error) => {
          console.error("Error fetching initial data:", error);
        });
    }
  }, [isAddMode, requestId]);

  const total = parseInt(totalFamilies, 10) || 0;
  // Places still open in the request; it locks once these are filled
  const remaining = Math.max(total - existingCount, 0);
  const filled = existingCount + forecasts.length;
  const selectedDonor = donors.find((donor) => String(donor.id) === String(donorId));

  // Handle input changes in editable fields
  const handleChange = (e, id) => {
    const { name, value } = e.target;

    setForecasts((prev) =>
      prev.map((forecast) =>
        forecast.id === id ? { ...forecast, [name]: value } : forecast
      )
    );
  };

  // Confirm and delete forecast entry
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are You Sure?",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        setForecasts((prev) => prev.filter((forecast) => forecast.id !== id));
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!isAddMode) {
      if (!donorId) {
        newErrors.donor_id = "Please select a donor for this request.";
      }
      if (!Number.isInteger(Number(totalFamilies)) || total < 1) {
        newErrors.requested_families = "Enter how many families can be added to this request.";
      } else if (total < forecasts.length) {
        newErrors.requested_families = `${forecasts.length} families are selected, total families cannot be less than that.`;
      }
    } else if (forecasts.length > remaining) {
      newErrors.families = `Only ${remaining} more families can be added to request ${rfqId}.`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit updated forecasts to backend
  const handleSubmit = () => {
    if (forecasts.length === 0) {
      Swal.fire({
        title: "Warning!",
        text: "Please Select At Least One Identifier To Submit Request",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (!validateForm()) return;

    const leftAfterSave = remaining - forecasts.length;
    Swal.fire({
      title: "Are You Sure?",
      text: (leftAfterSave > 0
        ? `${leftAfterSave} more families can be added to request ${rfqId} later.`
        : `Request ${rfqId} will be locked with ${total} families.`),
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedForecasts = forecasts.map((forecast) => ({
          people_id: forecast.id,
          scholarship_amount: forecast.scholarshipAmount || 0,
          incentive: forecast.partnerIncentive,
          medical_amount: forecast.medical_amount,
          meal_amount: forecast.meal_amount,
          utility_amount: forecast.utility_amount,
          rent_amount: forecast.rent_amount,
          other: forecast.other,
          affidavit_amount: forecast.affidavit_amount,
          business_amount: forecast.businessAmount,
          transportation_amount: forecast.transportationAmount,
          food_ration: forecast.food_ration,
        }));

        const save = isAddMode
          ? axios.post(`${process.env.REACT_APP_API_URL}/add-forecast-families/${requestId}`, {
              families: updatedForecasts,
            })
          : axios.post(`${process.env.REACT_APP_API_URL}/submit-forecast`, {
              donor_id: donorId,
              requested_families: total,
              families: updatedForecasts,
            });

        save
          .then(() => {
            sessionStorage.removeItem("selectedRows");
            Swal.fire({
              title: "Success!",
              text: isAddMode ? "Families Added To Fund Forecast Successfully." : "Family Fund Forecast Created Successfully.",
              icon: "success",
              confirmButtonText: "Ok",
            });
            navigate(isAddMode ? `/edit-family-fund-forecast/${requestId}` : "/family-forecast");
          })
          .catch((error) => {
            console.error("Error submitting data:", error);
            const serverErrors = error.response?.data?.errors;
            const messages = serverErrors ? [].concat(...Object.values(serverErrors)) : [];
            if (serverErrors) {
              setErrors(Object.fromEntries(Object.entries(serverErrors).map(([key, val]) => [key, [].concat(val).join(" ")])));
            }
            Swal.fire({
              title: "Error",
              html: messages.length > 0 ? messages.map((m) => `<div>${m}</div>`).join("") : "Failed to submit the forecast. Please try again.",
              icon: "error",
              confirmButtonText: "Ok",
            });
          });
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">{isAddMode ? "Add Families To Fund Forecast" : "Add Family Fund Forecast"}</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    home
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    Finances
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{isAddMode ? "Add Families To Fund Forecast" : "Add Family Fund Forecast"}</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <p className="p-3 text-center">
            <b>
              Total {forecasts.length}{" "}
              {forecasts.length > 1 ? "families are" : "family is"} ready for
              fund forecast.
            </b>
          </p>
          <hr />
        </Col>
      </Row>

      {forecasts.map((forecast, index) => (
        <div key={forecast.id} style={{ marginBottom: "20px" }}>
          <h4 className="m-3">Family Fund Forecast {index + 1}</h4>
          <Row>
            {/* Non-editable fields */}
            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Draft Family FSF-ID" className="mb-3">
                <Form.Control
                  type="text"
                  name="fsf_id"
                  value={forecast.fsf_id}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Family Identifier" className="mb-3">
                <Form.Control
                  type="text"
                  name="familyIdentifier"
                  value={forecast.familyIdentifier}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKiln"
                  value={forecast.brickKiln}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln Owner Name" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKilnOwnerName"
                  value={forecast.brickKilnOwnerName}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln Owner Cell" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKilnOwnerCell"
                  value={forecast.brickKilnOwnerCell}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln Owner CNIC" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKilnOwnerCnic"
                  value={forecast.brickKilnOwnerCnic}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Beneficiary Name" className="mb-3">
                <Form.Control
                  type="text"
                  name="name"
                  value={forecast.name}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Beneficiary CNIC" className="mb-3">
                <Form.Control
                  type="text"
                  name="cnic"
                  value={forecast.cnic}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Loan Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="loanAmount"
                  value={forecast.loanAmount}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            {/* Editable fields */}
            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Medical Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="medical_amount"
                  value={forecast.medical_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Food Ration" className="mb-3">
                <Form.Control
                  type="text"
                  name="food_ration"
                  value={forecast.food_ration}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Meal Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="meal_amount"
                  value={forecast.meal_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Utility Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="utility_amount"
                  value={forecast.utility_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Rent Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="rent_amount"
                  value={forecast.rent_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Affidavit Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="affidavit_amount"
                  value={forecast.affidavit_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Other Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="other"
                  value={forecast.other}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Transportation Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="transportationAmount"
                  value={forecast.transportationAmount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Business Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="businessAmount"
                  value={forecast.businessAmount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Scholarship Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="scholarshipAmount"
                  value={forecast.scholarshipAmount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Partner Incentive" className="mb-3">
                <Form.Control
                  type="text"
                  name="partnerIncentive"
                  value={forecast.partnerIncentive}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            {/* Delete button if more than one forecast */}
            {forecasts.length > 1 && (
              <Col md={4} className="d-flex align-items-center">
                <Button
                  variant="danger"
                  onClick={() => handleDelete(forecast.id)}
                  className="mt-3"
                >
                  <MdDelete style={{ fontSize: "20px", paddingTop: 0 }} /> Remove
                </Button>
              </Col>
            )}
          </Row>
        </div>
      ))}

      {/* Request details: donor and how many families this request can have */}
      <hr />
      <h4 className="m-3">
        Request Details{" "}
        {total > 0 && (
          <Badge bg={filled === total ? "success" : "warning"} text={filled === total ? undefined : "dark"}>
            Selected {filled} / {total}
          </Badge>
        )}
      </h4>
      <Row>
        <Form.Group as={Col} md={4}>
          <FloatingLabel label="Request ID" className="mb-3">
            <Form.Control type="text" value={rfqId || ""} readOnly />
          </FloatingLabel>
        </Form.Group>

        <Form.Group as={Col} md={4}>
          <FloatingLabel label="Donor" className="mb-3">
            <Form.Select value={donorId} onChange={(e) => setDonorId(e.target.value)} disabled={isAddMode}>
              <option value="">Select Donor</option>
              {donors.map((donor) => (
                <option key={donor.id} value={donor.id}>
                  {donor.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {errors.donor_id && <span className="text-danger">{errors.donor_id}</span>}
        </Form.Group>

        <Form.Group as={Col} md={4}>
          <FloatingLabel label="Total Families In This Request" className="mb-3">
            <Form.Control
              type="number"
              min={1}
              value={totalFamilies}
              onChange={(e) => setTotalFamilies(e.target.value)}
              readOnly={isAddMode}
            />
          </FloatingLabel>
          {errors.requested_families && <span className="text-danger">{errors.requested_families}</span>}
        </Form.Group>
      </Row>
      {isAddMode && (
        <p className="mx-3">
          Request {rfqId} for {selectedDonor?.name} already has {existingCount} of {total} families. {remaining} more can be added.
          {requestStatus?.id === 4 && " This request is approved, so the families added now will be approved directly."}
        </p>
      )}
      {(errors.families || errors.request) && (
        <p className="text-danger mx-3">{errors.families || errors.request}</p>
      )}

      {isLocked ? (
        <p className="p-3 text-center">
          <b>Request {rfqId} is locked. No more families can be added.</b>
        </p>
      ) : (
        <Button variant="primary" onClick={handleSubmit}>
          Submit
        </Button>
      )}
    </PageLayout>
  );
};

export default FamilyFundForecast;
